const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const PUBLIC_URL = process.env.PUBLIC_URL || "/";

export class APIClient {
  constructor() {
    this.baseUrl = API_URL.replace(/\/$/, "") + "/api";
    this.pollInterval = 2000; // Poll every 2 seconds
    this.defaultHeaders = {
      "ngrok-skip-browser-warning": "true",
    };
  }
  async getDocuments() {
    try {
      const response = await fetch(`${this.baseUrl}/documents/`, {
        headers: this.defaultHeaders,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching documents:", error);
      return [];
    }
  }
  async getDocumentById(id) {
    try {
      const response = await fetch(`${this.baseUrl}/documents/${id}`, {
        headers: this.defaultHeaders,
      });
      if (!response.ok) throw new Error("Failed to fetch document detail");
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching document by ID:", error);
      throw error;
    }
  }
  async updateDocument(id, updatedFields) {
    const response = await fetch(`${this.baseUrl}/documents/${id}`, {
      method: "PUT",
      headers: {
        ...this.defaultHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updatedFields),
    });

    if (!response.ok) throw new Error("Failed to update document");
    return await response.json();
  }
  async listDocuments() {
    try {
      const response = await fetch(`${this.baseUrl}/documents/`, {
        method: "GET",
        headers: this.defaultHeaders,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching documents:", error);
      return [];
    }
  }

  async getDocumentTypes() {
    try {
      const response = await fetch(`${this.baseUrl}/document-types/`, {
        method: "GET",
        headers: this.defaultHeaders,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching document types:", error);
      return [];
    }
  }

  async createDocumentType(documentType) {
    try {
      const response = await fetch(`${this.baseUrl}/document-types/`, {
        method: "POST",
        headers: {
          ...this.defaultHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(documentType),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error creating document type:", error);
      throw error;
    }
  }

  async deleteDocumentType(id) {
    try {
      const response = await fetch(`${this.baseUrl}/document-types/${id}`, {
        method: "DELETE",
        headers: this.defaultHeaders,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting document type:", error);
      throw error;
    }
  }

  async updateDocumentType(id, documentType) {
    try {
      const response = await fetch(`${this.baseUrl}/document-types/${id}`, {
        method: "PUT",
        headers: {
          ...this.defaultHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(documentType),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error updating document type:", error);
      throw error;
    }
  }

  async processDocument(file, documentTypeId, options = {}) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type_id", documentTypeId);

      const response = await fetch(`${this.baseUrl}/processing/document`, {
        method: "POST",
        headers: this.defaultHeaders,
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Failed to process document");
      }
      return data;
    } catch (error) {
      console.error("Error processing document:", error);
      return { success: false, error: error.message };
    }
  }

  async getAvailableModels() {
    try {
      const response = await fetch(`${this.baseUrl}/processing/models`, {
        headers: this.defaultHeaders,
      });
      if (!response.ok) throw new Error("Failed to fetch available models");
      const data = await response.json();
      return data;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getDocumentStatus(documentId) {
    try {
      const response = await fetch(`${this.baseUrl}/documents/${documentId}/status/`, {
        method: "GET",
        headers: this.defaultHeaders,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching document status:", error);
      return { success: false, error: error.message };
    }
  }

  async getDocument(documentId) {
    try {
      const response = await fetch(`${this.baseUrl}/documents/${documentId}`, {
        method: "GET",
        headers: this.defaultHeaders,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Ensure we have all the necessary data
      let parsedData = null;
      if (data.parsed_data) {
        try {
          // Handle both string and object formats
          const rawData =
            typeof data.parsed_data === "string" ? JSON.parse(data.parsed_data) : data.parsed_data;

          // Log for debugging
          console.log("Raw parsed data:", rawData);

          parsedData = rawData;
        } catch (e) {
          console.error("Error parsing document data:", e);
        }
      }

      return {
        ...data,
        success: true,
        content: data.content || "",
        parsed_data: parsedData,
        status: data.status || "Unknown",
      };
    } catch (error) {
      return {
        success: false,
        status: "Error",
        error: error.message,
      };
    }
  }

  async pollDocumentStatus(documentId, onUpdate, maxAttempts = 60) {
    let attempts = 0;
    let pollTimeout;

    const poll = async () => {
      try {
        const result = await this.getDocument(documentId);
        console.log("Poll result:", result);

        // Always call update callback with latest result
        if (result && typeof result === "object") {
          onUpdate(result);

          // Check for terminal states
          if (result.status === "Completed") {
            clearTimeout(pollTimeout);
            return result;
          } else if (result.status === "Error") {
            clearTimeout(pollTimeout);
            throw new Error(result.error || "Document processing failed");
          } else if (result.status === "Processing") {
            // Continue polling for Processing status
            if (attempts < maxAttempts) {
              attempts++;
              pollTimeout = setTimeout(() => poll(), this.pollInterval);
            } else {
              clearTimeout(pollTimeout);
              throw new Error("Document processing timeout - max attempts reached");
            }
          } else {
            // Unknown status
            clearTimeout(pollTimeout);
            throw new Error(`Unknown document status: ${result.status}`);
          }
        } else {
          throw new Error("Invalid response format from server");
        }
      } catch (error) {
        clearTimeout(pollTimeout);
        const errorResult = {
          success: false,
          status: "Error",
          error: error.message,
        };
        onUpdate(errorResult);
        return errorResult;
      }
    };

    return poll();
  }
}

export default new APIClient();
